import { FC } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useFormikContext } from "formik";
import moment from "moment";

import SelectOption from "types/SelectOption";
import { TStates } from "types/States";
import { MAX_INPUT_LENGTH } from "constants/form";
import { StateSelectOptions, zipInputFormat } from "constants/shared";
import { getFieldError, getPhoneNumberOrNull } from "helpers";

import {
  DatePicker,
  FormLabelSmall,
  Input,
  InputContainer,
  InputGroupContainerHalved,
  NumberInput,
  PhoneInput,
  SsnInput,
} from "uikit";
import { renderExternalLabel } from "uikit/Input/helpers";
import CustomSelect from "uikit/Input/Select";

import { KYCProfileFormType } from "./validationSchema";
import { Container } from "./styles";

interface IProps {
  originTranslationPrefix: string;
}

export const KYCProfileForm: FC<IProps> = ({ originTranslationPrefix }) => {
  const { t } = useTranslation();
  const formLabelTranslationPrefix = `${originTranslationPrefix}.shared.profile_form.form_labels`;

  const formikProps = useFormikContext<KYCProfileFormType>();
  const { values, setFieldValue, handleChange } = formikProps;

  return (
    <Container>
      <InputGroupContainerHalved>
        <InputContainer>
          <FormLabelSmall>
            <Trans i18nKey={`${formLabelTranslationPrefix}.first_name`} />
          </FormLabelSmall>
          <Input
            name="firstName"
            onChange={(e) => setFieldValue("firstName", e.currentTarget.value.trimStart())}
            onBlur={(e) => setFieldValue("firstName", e.currentTarget.value.trimEnd())}
            value={values.firstName}
            error={getFieldError("firstName", formikProps, {
              field: t(`${formLabelTranslationPrefix}.first_name`),
              maxInputLength: MAX_INPUT_LENGTH,
            })}
            errorDataTestId="error-first-name"
            disabled
          />
        </InputContainer>

        <InputContainer>
          <FormLabelSmall>
            <Trans i18nKey={`${formLabelTranslationPrefix}.last_name`} />
          </FormLabelSmall>
          <Input
            name="lastName"
            onChange={(e) => setFieldValue("lastName", e.currentTarget.value.trimStart())}
            onBlur={(e) => setFieldValue("lastName", e.currentTarget.value.trimEnd())}
            value={values.lastName}
            error={getFieldError("lastName", formikProps, {
              field: t(`${formLabelTranslationPrefix}.last_name`),
              maxInputLength: MAX_INPUT_LENGTH,
            })}
            errorDataTestId="error-last-name"
            disabled
          />
        </InputContainer>
      </InputGroupContainerHalved>

      <InputGroupContainerHalved>
        <InputContainer>
          <FormLabelSmall>
            <Trans i18nKey={`${formLabelTranslationPrefix}.phone`} />
          </FormLabelSmall>
          <PhoneInput
            name="phone"
            onChange={(value) => setFieldValue("phone", getPhoneNumberOrNull(value as string))}
            value={values.phone || undefined}
            error={getFieldError("phone", formikProps, {
              field: t(`${formLabelTranslationPrefix}.phone`),
            })}
            errorDataTestId="error-phone"
          />
        </InputContainer>

        <InputContainer>
          <FormLabelSmall>
            <Trans i18nKey={`${formLabelTranslationPrefix}.social_security_number`} />
          </FormLabelSmall>
          <SsnInput
            onSetFormField={(value: string) => setFieldValue("socialSecurityNumber", value)}
            error={getFieldError("socialSecurityNumber", formikProps, {
              field: t(`${formLabelTranslationPrefix}.social_security_number`),
            })}
            value={values.socialSecurityNumber || ""}
            placeholder={t(`common.input.placeholder_base`, {
              field: t(`${formLabelTranslationPrefix}.social_security_number`),
            })}
          />
        </InputContainer>
      </InputGroupContainerHalved>

      <InputGroupContainerHalved>
        <InputContainer>
          <FormLabelSmall>
            <Trans i18nKey={`${formLabelTranslationPrefix}.birth_date`} />
          </FormLabelSmall>
          <DatePicker
            useMothsYearsHeader={true}
            name="birthDate"
            selected={values.birthDate || null}
            onChange={(e) => setFieldValue("birthDate", e)}
            error={getFieldError("birthDate", formikProps, {
              field: t(`${formLabelTranslationPrefix}.birth_date`),
            })}
            maxDate={new Date()}
            minDate={moment().subtract(100, "year").toDate()}
            errorDataTestId="error-birthdate"
            placeholderText={t(`common.input.placeholder_base`, {
              field: t(`${formLabelTranslationPrefix}.birth_date`),
            })}
          />
        </InputContainer>

        <InputContainer>
          <FormLabelSmall>
            <Trans i18nKey={`${formLabelTranslationPrefix}.address`} />
          </FormLabelSmall>
          <Input
            name="address"
            onChange={handleChange}
            value={values.address}
            error={getFieldError("address", formikProps, {
              field: t(`${formLabelTranslationPrefix}.address`),
              maxInputLength: MAX_INPUT_LENGTH,
            })}
            errorDataTestId="error-address"
            placeholder={t(`common.input.placeholder_base`, {
              field: t(`${formLabelTranslationPrefix}.address`),
            })}
          />
        </InputContainer>
      </InputGroupContainerHalved>

      <InputGroupContainerHalved>
        <InputContainer>
          <FormLabelSmall>
            {renderExternalLabel(<Trans i18nKey={`${formLabelTranslationPrefix}.suite`} />, true)}
          </FormLabelSmall>
          <Input
            name="suite"
            optional
            onChange={handleChange}
            value={values.suite || ""}
            error={getFieldError("suite", formikProps, {
              field: t(`${formLabelTranslationPrefix}.suite`),
              maxInputLength: MAX_INPUT_LENGTH,
            })}
            errorDataTestId="error-suit"
            placeholder={t(`common.input.placeholder_base`, {
              field: t(`${formLabelTranslationPrefix}.suite`),
            })}
          />
        </InputContainer>

        <InputContainer>
          <FormLabelSmall>
            <Trans i18nKey={`${formLabelTranslationPrefix}.city`} />
          </FormLabelSmall>
          <Input
            name="city"
            onChange={handleChange}
            value={values.city || ""}
            error={getFieldError("city", formikProps, {
              field: t(`${formLabelTranslationPrefix}.city`),
              maxInputLength: MAX_INPUT_LENGTH,
            })}
            errorDataTestId="error-city"
            placeholder={t(`common.input.placeholder_base`, {
              field: t(`${formLabelTranslationPrefix}.city`),
            })}
          />
        </InputContainer>
      </InputGroupContainerHalved>

      <InputGroupContainerHalved>
        <InputContainer>
          <FormLabelSmall>
            <Trans i18nKey={`${formLabelTranslationPrefix}.state`} />
          </FormLabelSmall>
          <CustomSelect<SelectOption<TStates>>
            name="state"
            onChange={(option) => setFieldValue("state", option)}
            value={values.state as any}
            options={StateSelectOptions}
            error={getFieldError("state.value", formikProps, {
              field: t(`${formLabelTranslationPrefix}.state`),
            })}
            isSearchable={true}
            errorDataTestId="error-state"
            placeholder={t(`common.input.placeholder_base`, {
              field: t(`${formLabelTranslationPrefix}.state`),
            })}
          />
        </InputContainer>

        <InputContainer>
          <FormLabelSmall>
            <Trans i18nKey={`${formLabelTranslationPrefix}.zip`} />
          </FormLabelSmall>
          <NumberInput
            name="zip"
            onChange={(val) => setFieldValue("zip", val.toString().split(" ").join(""))}
            value={values.zip}
            error={getFieldError("zip", formikProps, {
              field: t(`${formLabelTranslationPrefix}.zip`),
            })}
            errorDataTestId="error-zip"
            format={zipInputFormat}
            placeholder={t(`common.input.placeholder_base`, {
              field: t(`${formLabelTranslationPrefix}.zip`),
            })}
          />
        </InputContainer>
      </InputGroupContainerHalved>
    </Container>
  );
};
